import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ChevronDown, ChevronUp, Filter, Loader2, Plus, Search, UserCheck, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Alert } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { DateInputBR } from '@components/DateInputBR';
import { Pagination } from '@components/Pagination';
import { PermissionGuard } from '@components/PermissionGuard';
import { Avatar, DueDateLabel, PriorityBadge, RecurrenceIcon } from '@components/activity/ActivityBits';
import { usePermissions } from '@hooks/usePermissions';
import { useProfile } from '@contexts/ProfileContext';
import { useActivityModal } from '@contexts/ActivityModalContext';
import activityService from '@services/activityService';
import boardService from '@services/boardService';
import { getConfig } from '../../config';
import type { Activity, ActivitySortBy, ActivityStatus, Pagination as PaginationInfo, Priority } from '@/types/activity';
import type { Board } from '@/types/board';
import { PRIORITIES, PRIORITY_LABELS } from '@/utils/priority';

const UNASSIGNED = 'none';

interface Filters {
  board: string;
  assignee: string;
  priority: Priority | '';
  status: ActivityStatus;
  overdue: boolean;
  noDueDate: boolean;
  search: string;
  dueFrom: string;
  dueTo: string;
}

const filtersFromParams = (params: URLSearchParams): Filters => ({
  board: params.get('board') ?? '',
  assignee: params.get('assignee') ?? '',
  priority: (params.get('priority') as Priority | null) ?? '',
  status: (params.get('status') as ActivityStatus | null) ?? 'open',
  overdue: params.get('overdue') === '1',
  noDueDate: params.get('no_due_date') === '1',
  search: params.get('search') ?? '',
  dueFrom: params.get('due_from') ?? '',
  dueTo: params.get('due_to') ?? '',
});

const SORTABLE: { key: ActivitySortBy; label: string }[] = [
  { key: 'title', label: 'Atividade' },
  { key: 'assignee', label: 'Responsável' },
  { key: 'priority', label: 'Prioridade' },
  { key: 'due_date', label: 'Prazo' },
  { key: 'board', label: 'Quadro / coluna' },
];

/**
 * Lista paginada de atividades com filtros. Gestor/admin seleciona várias e atribui de uma vez
 * (POST /activities/bulk-assign). `?open=<id>` abre a atividade direto no modal (link do sino).
 */
export const ActivityListView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkPermission } = usePermissions();
  const { canManageTeam, assignableMembers, self } = useProfile();
  const { openActivity, openNewActivity, version } = useActivityModal();
  const canBulkAssign = canManageTeam && checkPermission('activities.assign').hasPermission;

  const [filters, setFilters] = useState<Filters>(() => filtersFromParams(searchParams));
  const [searchInput, setSearchInput] = useState(filters.search);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<ActivitySortBy>('due_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [items, setItems] = useState<Activity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const pageSize = getConfig().ui.itemsPerPage || 20;

  // ?open=<id> → abre o modal e limpa o parâmetro.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && Number(openId) > 0) {
      openActivity(Number(openId));
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, openActivity]);

  useEffect(() => {
    boardService.getAll().then(setBoards).catch(setError);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await activityService.getAll({
        board_id: filters.board ? Number(filters.board) : undefined,
        assignee_member_id: filters.assignee && filters.assignee !== UNASSIGNED ? Number(filters.assignee) : undefined,
        unassigned: filters.assignee === UNASSIGNED,
        priority: filters.priority || undefined,
        status: filters.status,
        overdue: filters.overdue,
        no_due_date: filters.noDueDate,
        search: filters.search || undefined,
        due_from: filters.dueFrom || undefined,
        due_to: filters.dueTo || undefined,
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_direction: sortDirection,
      });
      setItems(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    load();
  }, [load, version]);

  // Seleção só vale para a página/filtro atual.
  useEffect(() => {
    setSelected(new Set());
  }, [filters, page]);

  const update = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(filtersFromParams(new URLSearchParams()));
    setSearchInput('');
    setPage(1);
  };

  const toggleSort = (key: ActivitySortBy) => {
    if (sortBy === key) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(key);
      setSortDirection(key === 'priority' ? 'desc' : 'asc');
    }
  };

  const allSelected = items.length > 0 && items.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((a) => a.id)));
  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleBulkAssign = async () => {
    if (!bulkAssignee || selected.size === 0) return;
    setBulkSaving(true);
    try {
      const result = await activityService.bulkAssign([...selected], Number(bulkAssignee));
      setBulkResult(
        `${result.updated} atividade(s) atribuída(s).` +
          (result.skipped.length > 0 ? ` ${result.skipped.length} ignorada(s) (sem permissão para delegar ou já com essa pessoa).` : ''),
      );
      setSelected(new Set());
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBulkSaving(false);
    }
  };

  const hasFilters = useMemo(() => {
    const d = filtersFromParams(new URLSearchParams());
    return JSON.stringify(d) !== JSON.stringify(filters);
  }, [filters]);

  const colSpan = canBulkAssign ? 6 : 5;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3">Atividades</h1>
        <PermissionGuard permission="activities.write">
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => openNewActivity()}>
            Nova atividade
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          <div className="relative sm:col-span-2">
            <label className="text-label block mb-1">Buscar</label>
            <Search size={16} className="absolute left-3 bottom-2.5 text-slate-400" />
            <input
              className="input-field pl-9"
              placeholder="Título ou descrição…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div>
            <label className="text-label block mb-1">Quadro</label>
            <select className="select-field" value={filters.board} onChange={(e) => update('board', e.target.value)}>
              <option value="">Todos</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          {canManageTeam && (
            <div>
              <label className="text-label block mb-1">Responsável</label>
              <select className="select-field" value={filters.assignee} onChange={(e) => update('assignee', e.target.value)}>
                <option value="">Todos</option>
                <option value={UNASSIGNED}>Sem responsável</option>
                {assignableMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.id === self?.id ? ' (eu)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-label block mb-1">Prioridade</label>
            <select
              className="select-field"
              value={filters.priority}
              onChange={(e) => update('priority', e.target.value as Priority | '')}
            >
              <option value="">Todas</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-label block mb-1">Situação</label>
            <select
              className="select-field"
              value={filters.status}
              onChange={(e) => update('status', e.target.value as ActivityStatus)}
            >
              <option value="open">Abertas</option>
              <option value="done">Concluídas</option>
              <option value="all">Todas</option>
            </select>
          </div>
          <div>
            <label className="text-label block mb-1">Prazo de</label>
            <DateInputBR className="input-field w-full" value={filters.dueFrom} onChange={(v) => update('dueFrom', v)} />
          </div>
          <div>
            <label className="text-label block mb-1">Prazo até</label>
            <DateInputBR
              className="input-field w-full"
              value={filters.dueTo}
              min={filters.dueFrom || undefined}
              onChange={(v) => update('dueTo', v)}
            />
          </div>
          <div className="flex items-end gap-4 sm:col-span-2 pb-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox-field"
                checked={filters.overdue}
                onChange={(e) => update('overdue', e.target.checked)}
              />
              Só atrasadas
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox-field"
                checked={filters.noDueDate}
                onChange={(e) => update('noDueDate', e.target.checked)}
              />
              Sem prazo
            </label>
          </div>
          {hasFilters && (
            <div className="flex items-end pb-1">
              <button type="button" onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                <X size={14} /> Limpar filtros
              </button>
            </div>
          )}
        </div>
      </div>

      {bulkResult && (
        <Alert variant="success" onClose={() => setBulkResult(null)}>
          {bulkResult}
        </Alert>
      )}

      {canBulkAssign && selected.size > 0 && (
        <div className="card p-3 flex flex-wrap items-center gap-3 border-emerald-200 bg-emerald-50">
          <span className="text-sm font-medium text-emerald-800">{selected.size} selecionada(s)</span>
          <select className="select-field w-60" value={bulkAssignee} onChange={(e) => setBulkAssignee(e.target.value)}>
            <option value="">Atribuir para…</option>
            {assignableMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            size="sm"
            icon={<UserCheck />}
            onClick={handleBulkAssign}
            loading={bulkSaving}
            disabled={!bulkAssignee}
          >
            Atribuir selecionadas
          </Button>
          <button type="button" className="text-sm text-slate-500 hover:text-slate-800" onClick={() => setSelected(new Set())}>
            Limpar seleção
          </button>
        </div>
      )}

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && items.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && items.length === 0 && <p className="text-center text-slate-600 py-8">Nenhuma atividade encontrada</p>}
        {items.map((a) => (
          <div
            key={a.id}
            className={clsx('card p-3 flex gap-3 cursor-pointer', selected.has(a.id) && 'ring-2 ring-emerald-300', loading && 'opacity-60')}
            onClick={() => openActivity(a.id)}
          >
            {canBulkAssign && (
              <input
                type="checkbox"
                className="checkbox-field mt-1"
                checked={selected.has(a.id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleOne(a.id)}
              />
            )}
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <span className={clsx('font-medium text-slate-800', a.is_done && 'line-through text-slate-400')}>{a.title}</span>
                <PriorityBadge priority={a.priority} />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small">
                <DueDateLabel activity={a} />
                <span className="inline-flex items-center gap-1">
                  <Avatar name={a.assignee_name} /> {a.assignee_name ?? 'Sem responsável'}
                </span>
                <span className="text-slate-500">
                  {a.board_name} · {a.column_name}
                </span>
              </div>
            </div>
          </div>
        ))}
        {pagination && (
          <Pagination page={pagination.page} totalPages={pagination.total_pages} totalItems={pagination.total_items} onChange={setPage} />
        )}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                {canBulkAssign && (
                  <th className="w-10">
                    <input type="checkbox" className="checkbox-field" checked={allSelected} onChange={toggleAll} />
                  </th>
                )}
                {SORTABLE.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => toggleSort(col.key)}
                    className="cursor-pointer hover:bg-slate-100 select-none"
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {sortBy === col.key &&
                        (sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={colSpan} className="table-cell text-center py-12">
                    <Filter size={32} className="text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-600 font-medium">Nenhuma atividade encontrada</p>
                    <p className="text-small">Ajuste os filtros de busca.</p>
                  </td>
                </tr>
              )}
              {items.map((a) => (
                <tr
                  key={a.id}
                  className={clsx('table-row cursor-pointer', selected.has(a.id) && 'bg-emerald-50/60', loading && 'opacity-60')}
                  onClick={() => openActivity(a.id)}
                >
                  {canBulkAssign && (
                    <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="checkbox-field"
                        checked={selected.has(a.id)}
                        onChange={() => toggleOne(a.id)}
                      />
                    </td>
                  )}
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <span className={clsx('font-medium text-slate-800', a.is_done && 'line-through text-slate-400')}>
                        {a.title}
                      </span>
                      <RecurrenceIcon activity={a} />
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <Avatar name={a.assignee_name} />
                      <span className={clsx(!a.assignee_name && 'text-slate-400')}>{a.assignee_name ?? 'Sem responsável'}</span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <PriorityBadge priority={a.priority} />
                  </td>
                  <td className="table-cell">
                    <DueDateLabel activity={a} />
                  </td>
                  <td className="table-cell">
                    <p className="text-slate-700">{a.board_name}</p>
                    <p className="text-small">{a.column_name}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total_items}
            onChange={setPage}
          />
        )}
      </div>

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
